import React, { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Citation } from '../../types/citation';
import { formatMetadataForDisplay } from '../../utils/citationMetadata';

interface CitationModalProps {
  citation: Citation | null;
  isOpen: boolean;
  onClose: () => void;
}

const CitationModal: React.FC<CitationModalProps> = ({ citation, isOpen, onClose }) => {
  // Handle ESC key press
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen || !citation) {
    return null;
  }

  // Format metadata for display if available
  const metadataDisplay = citation.metadataInfo 
    ? formatMetadataForDisplay(citation.metadataInfo)
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 dark:bg-black/70"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal */}
      <div 
        className="relative w-full max-w-4xl max-h-[80vh] bg-white dark:bg-dark-primary border border-neutral-dark dark:border-dark-border rounded-xl shadow-2xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="citation-modal-title"
        aria-describedby="citation-modal-content"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-dark dark:border-dark-border">
          <h3 
            id="citation-modal-title" 
            className="text-lg font-semibold text-text-primary dark:text-dark-textPrimary"
          >
            Citation {citation.id}{citation.title ? `: ${citation.title}` : ' Details'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-text-tertiary dark:text-dark-textTertiary hover:text-text-primary dark:hover:text-dark-textPrimary hover:bg-neutral dark:hover:bg-dark-secondary rounded-lg transition-colors duration-150"
            aria-label="Close citation details"
            autoFocus
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-8rem)]">
          <div className="space-y-6">
            
            {/* Top - Citation Description */}
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-text-primary dark:text-dark-textPrimary border-b border-neutral-dark dark:border-dark-border pb-2">
                Citation Information
              </h4>
              
              <div className="prose dark:prose-invert prose-neutral max-w-none prose-sm">
                <div className="bg-neutral/30 dark:bg-dark-secondary/30 rounded-lg p-4 border border-neutral-dark dark:border-dark-border">
                  <ReactMarkdown>
                    {citation.detailedDescription || citation.content || 'No detailed information available for this citation.'}
                  </ReactMarkdown>
                </div>
              </div>
            </div>

            {/* Bottom - Source Data Fields Table */}
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-text-primary dark:text-dark-textPrimary border-b border-neutral-dark dark:border-dark-border pb-2">
                Source Data Fields
              </h4>
              
              {citation.sourceInfo && citation.sourceInfo.length > 0 ? (
                <div className="bg-neutral/30 dark:bg-dark-secondary/30 rounded-lg border border-neutral-dark dark:border-dark-border overflow-hidden">
                  <div className="overflow-x-auto max-h-96 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-neutral dark:bg-dark-secondary sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium text-text-primary dark:text-dark-textPrimary border-b border-neutral-dark dark:border-dark-border">
                            Source Field
                          </th>
                          <th className="px-4 py-3 text-left font-medium text-text-primary dark:text-dark-textPrimary border-b border-neutral-dark dark:border-dark-border">
                            Primary Source(s)
                          </th>
                          <th className="px-4 py-3 text-left font-medium text-text-primary dark:text-dark-textPrimary border-b border-neutral-dark dark:border-dark-border">
                            Description
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {citation.sourceInfo.map((source, index) => (
                          <tr key={index} className="border-b border-neutral-dark/30 dark:border-dark-border/30 last:border-b-0 hover:bg-neutral/20 dark:hover:bg-dark-secondary/20">
                            <td className="px-4 py-3 font-mono text-text-primary dark:text-dark-textPrimary">
                              {source.sourceField}
                            </td>
                            <td className="px-4 py-3 text-text-secondary dark:text-dark-textSecondary">
                              {source.primarySources && Array.isArray(source.primarySources) 
                                ? source.primarySources.join(', ')
                                : (source.primarySources || '')}
                            </td>
                            <td className="px-4 py-3 text-text-secondary dark:text-dark-textSecondary">
                              {source.description || 'No description available'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-text-tertiary dark:text-dark-textTertiary italic">
                  No source data fields available for this citation.
                </div>
              )}
            </div>
            
            {/* Legacy Source Metadata Section - only show if no sourceInfo available */}
            {(!citation.sourceInfo || citation.sourceInfo.length === 0) && metadataDisplay.length > 0 && (
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-text-primary dark:text-dark-textPrimary border-b border-neutral-dark dark:border-dark-border pb-2">
                  Source Metadata
                </h4>
                <div className="bg-neutral/30 dark:bg-dark-secondary/30 rounded-lg p-4">
                  <table className="w-full text-sm">
                    <tbody className="space-y-2">
                      {metadataDisplay.map((item, index) => (
                        <tr key={index} className="border-b border-neutral-dark/30 dark:border-dark-border/30 last:border-b-0">
                          <td className="py-2 pr-4 font-medium text-text-primary dark:text-dark-textPrimary align-top">
                            {item.key}
                          </td>
                          <td className="py-2 text-text-secondary dark:text-dark-textSecondary">
                            {item.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-neutral-dark dark:border-dark-border bg-neutral/30 dark:bg-dark-secondary/30">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors duration-150"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CitationModal;
